import VueRouter from 'vue-router';

import auth from '../api/auth';
import login from './login/login.routes';
import event from './event/event.routes';
import dashboard from './dashboard/dashboard.routes';
import authRoute from './auth/auth.routes';
import { frontendURL } from '../helper/URLHelper';

const routes = [
  ...event.routes,
  ...login.routes,
  ...dashboard.routes,
  ...authRoute.routes,
  {
    path: '/',
    redirect: '/app',
  },
];

window.roleWiseRoutes = {
  agent: [],
  administrator: [],
};

// generateRoleWiseRoute - updates window object with agent/admin route
const generateRoleWiseRoute = route => {
  route.forEach(element => {
    if (element.children) {
      generateRoleWiseRoute(element.children);
    }
    if (element.roles) {
      element.roles.forEach(roleEl => {
        window.roleWiseRoutes[roleEl].push(element.name);
      });
    }
  });
};
// Create a object of routes
// accessible by each role.
// returns an object with roles as keys and routeArr as values
generateRoleWiseRoute(routes);

export const router = new VueRouter({ mode: 'history', routes });

const unProtectedRoutes = ['login', 'auth_signup', 'auth_reset_password', 'event', 'vault'];

const authIgnoreRoutes = [
  'auth_confirmation',
  'pushBack',
  'auth_password_edit',
];

// Extract the numeric account id from an /app/accounts/:id/... path, or null.
const accountIdFromPath = path => {
  const match = /\/app\/accounts\/(\d+)(\/|$)/.exec(path || '');
  return match ? match[1] : null;
};

function routeIsAccessibleFor(route, role) {
  if (!role) return false;
  const accessible = window.roleWiseRoutes[role];
  if (!Array.isArray(accessible)) return false;
  return accessible.includes(route);
}

const routeValidators = [
  {
    protected: false,
    loggedIn: true,
    handler: () => 'dashboard',
  },
  {
    protected: true,
    loggedIn: false,
    handler: () => 'login',
  },
  {
    protected: true,
    loggedIn: true,
    handler: to => {
      const user = auth.getCurrentUser();
      const role = user?.role || user?.accounts?.[0]?.role;
      const isAccessible = routeIsAccessibleFor(to, role);
      if (isAccessible) return null;
      if (user?.account_id) {
        return frontendURL(`accounts/${user.account_id}/dashboard`);
      }
      return 'login';
    },
  },
  {
    protected: true,
    loggedIn: false,
    handler: () => 'event',
  },
  {
    protected: false,
    loggedIn: false,
    handler: () => null,
  },
];

export const validateAuthenticateRoutePermission = (to, from, next) => {
  const isLoggedIn = auth.isLoggedIn();
  const isProtectedRoute = !unProtectedRoutes.includes(to.name);
  const strategy = routeValidators.find(
    validator =>
      validator.protected === isProtectedRoute &&
      validator.loggedIn === isLoggedIn
  );
  const nextRoute = strategy.handler(to.name);
  return nextRoute ? next(frontendURL(nextRoute)) : next();
};

const validateRouteAccess = (to, from, next) => {
  // Hard-block signup route regardless of config to eliminate stray create-account entry points.
  if (to.name === 'auth_signup') {
    return next('/app/login');
  }

  if (
    window.chatwootConfig.signupEnabled !== 'true' &&
    to.meta &&
    to.meta.requireSignupEnabled && to.name !== 'event'
    && to.name !== 'vault'
  ) {
    const user = auth.getCurrentUser();
    // If we don't yet have a hydrated user, send the visitor to login instead
    // of pushing them into an undefined account route which can trigger guard
    // loops and "Create new account" prompts.
    if (!user || !user.account_id) {
      return next('/app/login?error=no_account');
    }
    return next(frontendURL(`accounts/${user.account_id}/dashboard`));
  }

  // Logged in but no route matched or role not yet hydrated: force account-scoped dashboard.
  if (auth.isLoggedIn()) {
    const user = auth.getCurrentUser();
    const accountId = user?.account_id || user?.accounts?.[0]?.id;
    if (accountId && to.path === '/app') {
      return next(frontendURL(`accounts/${accountId}/dashboard`));
    }
  }

  if (authIgnoreRoutes.includes(to.name)) {
    return next();
  }
  return validateAuthenticateRoutePermission(to, from, next);
};

// protecting routes
router.beforeEach((to, from, next) => {
  // Multi-account isolation guard.
  //
  // Account-scoped Vuex state (conversations, contacts, etc.) is keyed by the
  // per-account display_id, which is NOT globally unique: two different
  // accounts can each have conversation #909. For users who belong to more than
  // one account, an in-app navigation (or browser back/forward) that changes the
  // /accounts/:id/ segment WITHOUT a full page load leaves the previous
  // account's records in the store, so the new account's route can render mixed
  // data (one account's conversation list beside another account's message
  // thread). Forcing a full document load whenever the account segment changes
  // rebuilds the store from scratch for the target account and makes cross-
  // account bleed impossible.
  const toAccountId = accountIdFromPath(to.path);
  const fromAccountId = accountIdFromPath(from.path);
  if (toAccountId && fromAccountId && toAccountId !== fromAccountId) {
    window.location.assign(to.fullPath);
    return;
  }

  if (!to.name) {
    const user = auth.getCurrentUser();
    const targetAccountId = user?.account_id || user?.accounts?.[0]?.id;
    if (targetAccountId) {
      const target = frontendURL(`accounts/${targetAccountId}/dashboard`);
      if (to.path === target || from.path === target) return next();
      return next(target);
    }
    // No viable user/account — force a clean login instead of looping.
    return next('/app/login?error=no_account');
  }

  return validateRouteAccess(to, from, next);
});

export default router;
